import { Navigate, Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { useDeveloperProjects } from '@/hooks'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { Skeleton } from '@/components/ui/skeleton'
import { UserAvatar } from '@/components/UserAvatar'

function formatDate(input: string) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(input))
  } catch (error) {
    return input
  }
}

export default function BountyBoard() {
  const { user } = useAuth()
  const role = user?.profile?.role

  if (role && role !== 'developer') {
    return <Navigate to="/projects" replace />
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const developerProjects = useDeveloperProjects()

  if (developerProjects.isLoading) {
    return (
      <div className="container mx-auto max-w-6xl space-y-6 px-4 py-10">
        <Skeleton className="h-9 w-72" />
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-64 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (developerProjects.error) {
    return (
      <div className="container mx-auto max-w-4xl space-y-4 px-4 py-16 text-center text-destructive">
        Unable to load bounty board projects right now. Please refresh the page.
      </div>
    )
  }

  const projects = developerProjects.data?.public_projects ?? []

  return (
    <div className="container mx-auto max-w-6xl space-y-8 px-4 py-10">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Bounty board</h1>
        <p className="max-w-3xl text-muted-foreground">
          Explore public projects looking for expertise. Click into a brief to review requirements
          and prepare a proposal.
        </p>
      </header>

      {projects.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground">
            <p className="text-lg font-semibold">No public projects are available right now.</p>
            <p className="text-sm">Check back soon or keep an eye on your direct invitations.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {projects.map((project) => (
            <Card key={project.id} className="flex h-full flex-col">
              <CardHeader className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <UserAvatar
                      size="sm"
                      avatarUrl={project.account_avatar_url}
                      name={project.account_name}
                      className="mt-0.5"
                    />
                    <div>
                      <CardTitle className="text-lg font-semibold leading-tight">
                        {project.title}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">{project.account_name}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {project.status.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Updated {formatDate(project.updated_at)}
                </p>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col justify-between gap-4 text-sm text-muted-foreground">
                <p className="line-clamp-5 leading-relaxed">{project.description}</p>
                <div className="space-y-2 text-xs">
                  <p>
                    Budget:{' '}
                    {project.budget_min || project.budget_max
                      ? `${project.budget_min ? `$${project.budget_min}` : 'Flexible'} ${project.budget_max ? `– $${project.budget_max}` : ''}`
                      : 'Not provided'}
                  </p>
                  <p>{project.proposals_count} proposal(s)</p>
                  {project.skills.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {project.skills.map((skill) => (
                        <Badge key={skill.id} variant="outline">
                          {skill.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <Button asChild size="sm" className="self-start">
                  <Link to={`/projects/${project.id}`}>View project</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
